<script setup lang="ts">
    import { computed } from 'vue';
    import {
        QuestionType,
        QuestionOptionsDisplay,
        type Question,
        type SurveyAnswerResource,
        type EventParticipantAnswerData,
        type AttestationAnswer,
    } from '../../SurveysTypes.d';
    import QuestionAnswerGlyphiconOptions from './QuestionAnswerGlyphiconOptions.vue';
    import QuestionAnswerChoiceOptions from './QuestionAnswerChoiceOptions.vue';
    import QuestionAnswerFreeText from './QuestionAnswerFreeText.vue';
    import QuestionAnswerMultipleNumeric from './QuestionAnswerMultipleNumeric.vue';
    import QuestionAnswerEventParticipant from './QuestionAnswerEventParticipant.vue';
    import QuestionAnswerAttestation from './QuestionAnswerAttestation.vue';
    import QuestionAnswerFileUpload from './QuestionAnswerFileUpload.vue';
    import { isImageFile } from '../use-survey-resource-previews';

    // Renders one respondent's answer to a question, for every question type.
    // Shared by the survey statistics response detail and the event guest list response modal.

    const props = defineProps<{
        question: Question;
        freeText: string | null;
        answerOptions: { QuestionOptionId: string; NumericResponseValue: number | null }[];
        resources: SurveyAnswerResource[];
        responseId: string;
        resourcePreviewUrls: Record<string, string>;
    }>();

    const type = computed(() => props.question.Type);

    const answeredOptionIds = computed(() => new Set(props.answerOptions.map((o) => o.QuestionOptionId)));

    const numericOptions = computed(() =>
        [...(props.question.QuestionOptions ?? [])]
            .sort((a, b) => a.Ordinal - b.Ordinal)
            .map((option) => ({
                QuestionOptionId: option.QuestionOptionId,
                Value: option.Value,
                NumericResponseValue: props.answerOptions.find((o) => o.QuestionOptionId === option.QuestionOptionId)?.NumericResponseValue ?? null,
            }))
    );

    const eventParticipants = computed<EventParticipantAnswerData | null>(() => {
        if (!props.freeText) return null;
        try {
            return JSON.parse(props.freeText);
        } catch {
            return null;
        }
    });

    const attestation = computed<AttestationAnswer | null>(() => {
        if (!props.freeText) return null;
        const parts = props.freeText.split(';');
        const name = [parts[0], parts[1]].filter((s) => s && s.trim()).join(' ');
        const email = (parts[2] ?? '').trim();
        const message = (parts[3] ?? '').trim();
        return { name, email, message };
    });
</script>

<template>
    <QuestionAnswerGlyphiconOptions
        v-if="question.OptionsDisplay === QuestionOptionsDisplay.Glyphicon && question.QuestionOptions?.length"
        :question="question"
        :answered-option-ids="answeredOptionIds" />

    <QuestionAnswerChoiceOptions
        v-else-if="[QuestionType.Single, QuestionType.Multiple, QuestionType.Matrix].includes(type) && question.QuestionOptions?.length"
        :question="question"
        :answered-option-ids="answeredOptionIds" />

    <QuestionAnswerFreeText v-if="[QuestionType.FreeText, QuestionType.InputField, QuestionType.Hidden].includes(type)" :free-text="freeText" />

    <QuestionAnswerMultipleNumeric v-if="type === QuestionType.MultipleNumeric" :numeric-options="numericOptions" />

    <QuestionAnswerEventParticipant v-if="type === QuestionType.EventParticipant" :event-participants="eventParticipants" />

    <QuestionAnswerAttestation v-if="type === QuestionType.Attestation" :attestation="attestation" />

    <QuestionAnswerFileUpload
        v-if="type === QuestionType.FileUpload"
        :response-id="responseId"
        :question-id="question.QuestionId"
        :resources="resources"
        :resource-preview-urls="resourcePreviewUrls"
        :is-image-file="isImageFile" />
</template>
